"use client";

import { c as _c } from "react/compiler-runtime";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { DROP_POLL_VOTED_NOTIFICATION_CAUSE, type NotificationCause } from "@/types/feed.types";
import { useMemo } from "react";
import NotificationsCauseFilter from "./NotificationsCauseFilter";
import { useNotificationsController } from "./hooks/useNotificationsController";
import { useNotificationsScroll } from "./hooks/useNotificationsScroll";
import NotificationsContent from "./subcomponents/NotificationsContent";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
interface NotificationsProps {
  readonly activeDrop: ActiveDropState | null;
  readonly setActiveDrop: (activeDrop: ActiveDropState | null) => void;
}
const NOTIFICATION_CAUSE_PRIORITY: Record<NotificationCause, number> = {
  [ApiNotificationCause.IdentitySubscribed]: 0,
  [ApiNotificationCause.IdentityMentioned]: 1,
  [ApiNotificationCause.IdentityRep]: 2,
  [ApiNotificationCause.IdentityNic]: 3,
  [ApiNotificationCause.DropQuoted]: 4,
  [ApiNotificationCause.DropReplied]: 5,
  [ApiNotificationCause.DropVoted]: 6,
  [DROP_POLL_VOTED_NOTIFICATION_CAUSE]: 7,
  [ApiNotificationCause.DropBoosted]: 8,
  [ApiNotificationCause.DropReacted]: 9,
  [ApiNotificationCause.WaveCreated]: 10,
  [ApiNotificationCause.AllDrops]: 11,
  [ApiNotificationCause.PriorityAlert]: 12
};
const compareNotificationCause = (firstCause: NotificationCause, secondCause: NotificationCause): number => NOTIFICATION_CAUSE_PRIORITY[firstCause] - NOTIFICATION_CAUSE_PRIORITY[secondCause];
export default function Notifications(t0) {
  const $ = _c(45);
  const {
    activeDrop,
    setActiveDrop
  } = t0;
  const {
    activeFilter,
    setActiveFilter,
    isAuthenticated,
    notificationsViewStyle,
    items,
    isFetchingNextPage,
    pagination,
    contentState,
    handlers,
    markNotificationIdsAsRead
  } = useNotificationsController();
  let t1;
  if ($[0] !== activeFilter) {
    t1 = typeof activeFilter?.cause.length === "number" && activeFilter.cause.length > 0 ? [...activeFilter.cause].sort(compareNotificationCause).join("|") : "notifications-filter-all";
    $[0] = activeFilter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeFilterKey = t1;
  let t2;
  if ($[2] !== activeFilterKey || $[3] !== contentState.showErrorState || $[4] !== contentState.showLoader || $[5] !== contentState.showNoItems || $[6] !== isAuthenticated || $[7] !== isFetchingNextPage || $[8] !== items || $[9] !== pagination.fetchNextPage || $[10] !== pagination.hasNextPage) {
    t2 = {
      items,
      isAuthenticated,
      isFetchingNextPage,
      hasNextPage: pagination.hasNextPage,
      fetchNextPage: pagination.fetchNextPage,
      showLoader: contentState.showLoader,
      showNoItems: contentState.showNoItems,
      showErrorState: contentState.showErrorState,
      activeFilterKey
    };
    $[2] = activeFilterKey;
    $[3] = contentState.showErrorState;
    $[4] = contentState.showLoader;
    $[5] = contentState.showNoItems;
    $[6] = isAuthenticated;
    $[7] = isFetchingNextPage;
    $[8] = items;
    $[9] = pagination.fetchNextPage;
    $[10] = pagination.hasNextPage;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    scrollContainerRef,
    handleTopIntersection
  } = useNotificationsScroll(t2);
  let t3;
  if ($[12] !== activeFilter || $[13] !== isAuthenticated || $[14] !== setActiveFilter) {
    t3 = isAuthenticated ? <NotificationsCauseFilter activeFilter={activeFilter} setActiveFilter={setActiveFilter} /> : null;
    $[12] = activeFilter;
    $[13] = isAuthenticated;
    $[14] = setActiveFilter;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== activeDrop || $[17] !== contentState.hasConnectedProfile || $[18] !== contentState.hasProfileHandle || $[19] !== contentState.isLoadingProfile || $[20] !== contentState.resolvedErrorMessage || $[21] !== contentState.showErrorState || $[22] !== contentState.showLoader || $[23] !== contentState.showNoItems || $[24] !== contentState.showProxyDisabledState || $[25] !== handlers.handleAuthRetry || $[26] !== handlers.handleProxyDisable || $[27] !== handlers.handleRetry || $[28] !== isFetchingNextPage || $[29] !== items || $[30] !== markNotificationIdsAsRead || $[31] !== setActiveDrop) {
    t4 = <NotificationsContent isLoadingProfile={contentState.isLoadingProfile} hasConnectedProfile={contentState.hasConnectedProfile} hasProfileHandle={contentState.hasProfileHandle} showProxyDisabledState={contentState.showProxyDisabledState} showErrorState={contentState.showErrorState} resolvedErrorMessage={contentState.resolvedErrorMessage} handleRetry={handlers.handleRetry} handleAuthRetry={handlers.handleAuthRetry} handleProxyDisable={handlers.handleProxyDisable} showLoader={contentState.showLoader} showNoItems={contentState.showNoItems} items={items} loadingOlder={isFetchingNextPage} activeDrop={activeDrop} setActiveDrop={setActiveDrop} markNotificationIdsAsRead={markNotificationIdsAsRead} />;
    $[16] = activeDrop;
    $[17] = contentState.hasConnectedProfile;
    $[18] = contentState.hasProfileHandle;
    $[19] = contentState.isLoadingProfile;
    $[20] = contentState.resolvedErrorMessage;
    $[21] = contentState.showErrorState;
    $[22] = contentState.showLoader;
    $[23] = contentState.showNoItems;
    $[24] = contentState.showProxyDisabledState;
    $[25] = handlers.handleAuthRetry;
    $[26] = handlers.handleProxyDisable;
    $[27] = handlers.handleRetry;
    $[28] = isFetchingNextPage;
    $[29] = items;
    $[30] = markNotificationIdsAsRead;
    $[31] = setActiveDrop;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== handleTopIntersection || $[34] !== isFetchingNextPage || $[35] !== pagination.hasNextPage || $[36] !== scrollContainerRef || $[37] !== t4) {
    t5 = <WaveDropsReverseContainer ref={scrollContainerRef} onTopIntersection={handleTopIntersection} isFetchingNextPage={isFetchingNextPage} hasNextPage={pagination.hasNextPage} containerClassName="tw-bg-transparent">{t4}</WaveDropsReverseContainer>;
    $[33] = handleTopIntersection;
    $[34] = isFetchingNextPage;
    $[35] = pagination.hasNextPage;
    $[36] = scrollContainerRef;
    $[37] = t4;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  let t6;
  if ($[39] !== t3 || $[40] !== t5) {
    t6 = <div className="tw-relative tw-flex tw-h-full tw-flex-1 tw-flex-col tw-px-2 sm:tw-px-4 md:tw-px-6 lg:tw-px-0">{t3}{t5}</div>;
    $[39] = t3;
    $[40] = t5;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] !== notificationsViewStyle || $[43] !== t6) {
    t7 = <div className="scroll-shadow tw-relative tw-flex tw-flex-col tw-overflow-x-hidden tw-rounded-t-xl" style={notificationsViewStyle}>{t6}</div>;
    $[42] = notificationsViewStyle;
    $[43] = t6;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  return t7;
}